// Cross-server COPY/MOVE — third-party copy (TPC) from a browser origin.
// Mirrors the node server's crossServerCopy.mjs middleware (the TPC
// TransferHeader* convention from CERN grid storage): the client sends
// `TransferHeaderAuthorization: Bearer <dest-token>`, this origin strips the
// generic `TransferHeader` prefix and forwards the remainder (→ Authorization
// etc.) on outbound HEAD/MKCOL/PUT to the destination, streaming source
// content via fetch(). Supersedes the earlier "out of scope" decision —
// see browserdav-nephele-parity in acequiaDocumentation.
//
// Browser platform constraints honored here:
// - Blob bodies from handle.getFile() (the browser streams from disk);
//   Content-Length is a forbidden request header and is auto-computed.
// - No ReadableStream/duplex uploads (HTTP/1.1 destinations reject them).
// - Mixed content: an https page can only target https (or loopback)
//   destinations — the fetch fails before CORS; surfaces as 502.

// Max concurrent outbound transfers per tab (mirrors the node server).
import { forgetContentEtags } from './etag.js'

const MAX_CONCURRENT_TRANSFERS = 4
let _activeTransfers = 0

// Max recursion depth for directory copies.
const MAX_COPY_DEPTH = 50
const MAX_REDIRECTS = 3

/**
 * Strip the generic `transferheader` prefix from every header that carries
 * it and return the forwarded header set for outbound requests. The source
 * request's own Authorization is never forwarded.
 *   transferheaderauthorization → Authorization
 */
export function extractTransferHeaders(lowercaseHeaders = {}) {
    const forwarded = {}
    const prefix = 'transferheader'
    for (const [key, value] of Object.entries(lowercaseHeaders)) {
        if (key.startsWith(prefix) && key.length > prefix.length) {
            const stripped = key.slice(prefix.length)
            forwarded[stripped.charAt(0).toUpperCase() + stripped.slice(1)] = value
        }
    }
    return forwarded
}

/**
 * Decide how to treat a Destination header.
 *   local  — bare path, or absolute URL on a self host whose pathname starts
 *            with this request's route prefix (same instance)
 *   remote — any other absolute URL (foreign server, or another group /
 *            instance reached through the same relay host — the relay is a
 *            real HTTP endpoint, so TPC handles peer destinations too)
 * Returns { kind: 'local', pathname } | { kind: 'remote', url } |
 * { kind: 'invalid', reason }.
 */
export function classifyDestination({ destHeader, routePrefix = '', selfHosts = [] }) {
    if (!destHeader) return { kind: 'invalid', reason: 'Missing Destination header' }

    if (!/^https?:\/\//i.test(destHeader)) {
        if (!destHeader.startsWith('/')) {
            return { kind: 'invalid', reason: 'Destination must be an absolute path or URL' }
        }
        return { kind: 'local', pathname: decodeURIComponent(destHeader) }
    }

    let url
    try {
        url = new URL(destHeader)
    } catch {
        return { kind: 'invalid', reason: 'Malformed Destination URL' }
    }

    const pathname = decodeURIComponent(url.pathname)
    if (selfHosts.includes(url.host) && routePrefix && pathname.startsWith(routePrefix)) {
        return { kind: 'local', pathname }
    }
    return { kind: 'remote', url }
}

function multistatusBody(entries) {
    const responses = entries
        .map(
            (e) => `  <d:response>
    <d:href>${e.href}</d:href>
    <d:status>HTTP/1.1 ${e.status} ${e.statusText || ''}</d:status>${
        e.description
            ? `
    <d:responsedescription>${e.description}</d:responsedescription>`
            : ''
    }
  </d:response>`
        )
        .join('\n')
    return `<?xml version="1.0" encoding="utf-8"?>\n<d:multistatus xmlns:d="DAV:">\n${responses}\n</d:multistatus>`
}

const XML_HEADERS = { 'Content-Type': 'text/xml; charset=utf-8' }

function transferTimeoutMs(size) {
    return 30_000 + Math.ceil((size || 0) / 1_000_000) * 1000
}

function appendSegment(baseUrl, name) {
    const base = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/'
    return base + encodeURIComponent(name)
}

async function putFile(fileHandle, destHref, forwardedHeaders, overallSignal) {
    const file = await fileHandle.getFile()
    const signal = overallSignal
        ? AbortSignal.any([overallSignal, AbortSignal.timeout(transferTimeoutMs(file.size))])
        : AbortSignal.timeout(transferTimeoutMs(file.size))
    try {
        const resp = await fetch(destHref, {
            method: 'PUT',
            headers: { ...forwardedHeaders, 'Content-Type': 'application/octet-stream' },
            body: file,
            signal,
        })
        if (resp.ok) return { ok: true, status: resp.status }
        const text = await resp.text().catch(() => '')
        return { ok: false, status: resp.status, description: text.slice(0, 200) }
    } catch (error) {
        if (error.name === 'AbortError' || error.name === 'TimeoutError') {
            return { ok: false, status: 504, description: 'Transfer timed out' }
        }
        return { ok: false, status: 502, description: error.message || 'Network error' }
    }
}

async function transferDirectory(dirHandle, destHref, forwardedHeaders, results, depth) {
    if (depth > MAX_COPY_DEPTH) {
        results.push({ href: destHref, status: 500, description: 'Max recursion depth exceeded' })
        return
    }

    // MKCOL the collection; 405 = already exists, tolerated like the server.
    try {
        const mkcolResp = await fetch(destHref.endsWith('/') ? destHref : destHref + '/', {
            method: 'MKCOL',
            headers: forwardedHeaders,
            signal: AbortSignal.timeout(30_000),
        })
        if (!mkcolResp.ok && mkcolResp.status !== 405) {
            const text = await mkcolResp.text().catch(() => '')
            results.push({ href: destHref, status: mkcolResp.status, description: text.slice(0, 200) })
            return
        }
    } catch (error) {
        results.push({ href: destHref, status: 502, description: error.message || 'MKCOL network error' })
        return
    }

    const files = []
    const dirs = []
    for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') files.push(entry)
        else dirs.push(entry)
    }

    // Files with bounded concurrency (4), like the server.
    const queue = [...files]
    const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
        while (queue.length > 0) {
            const entry = queue.shift()
            if (!entry) break
            const fileHref = appendSegment(destHref, entry.name)
            const result = await putFile(entry, fileHref, forwardedHeaders, null)
            if (!result.ok) {
                results.push({ href: fileHref, status: result.status, description: result.description })
            }
        }
    })
    await Promise.all(workers)

    // Directories depth-first, sequentially.
    for (const sub of dirs) {
        await transferDirectory(sub, appendSegment(destHref, sub.name), forwardedHeaders, results, depth + 1)
    }
}

/**
 * Execute a cross-server COPY or MOVE. Returns a response envelope
 * { status, headers?, body, sourceDeleted } with the node server's status
 * mapping: 201 created / 204 overwrote (files), 201 all-success (dirs),
 * 207 per-resource failures, 404 missing source, 412 Overwrite:F conflict,
 * 502 network, 503 concurrency, 504 timeout.
 */
export async function crossServerCopyMove({
    method,
    srcPath,
    destUrl,
    headers = {},
    fileSystemHandler,
    overwrite = true,
}) {
    let srcHandle
    try {
        srcHandle = await fileSystemHandler.getFileHandle(srcPath)
    } catch {
        return {
            status: 404,
            headers: XML_HEADERS,
            body: `<?xml version="1.0" encoding="utf-8"?>\n<d:error xmlns:d="DAV:"><d:message>Source resource not found.</d:message></d:error>`,
            sourceDeleted: false,
        }
    }

    const forwardedHeaders = extractTransferHeaders(headers)
    const destHref = destUrl.href

    // Overwrite: F → HEAD precheck; network failures on the HEAD are
    // swallowed (assume the destination does not exist), like the server.
    if (!overwrite) {
        try {
            const headResp = await fetch(destHref, {
                method: 'HEAD',
                headers: forwardedHeaders,
                signal: AbortSignal.timeout(15_000),
            })
            if (headResp.ok) {
                return {
                    status: 412,
                    headers: XML_HEADERS,
                    body: `<?xml version="1.0" encoding="utf-8"?>\n<d:error xmlns:d="DAV:"><d:message>Destination resource already exists and Overwrite is F.</d:message></d:error>`,
                    sourceDeleted: false,
                }
            }
        } catch {
            // proceed
        }
    }

    if (_activeTransfers >= MAX_CONCURRENT_TRANSFERS) {
        return {
            status: 503,
            headers: { 'Retry-After': '5' },
            body: 'Too many concurrent transfers',
            sourceDeleted: false,
        }
    }

    _activeTransfers++
    try {
        if (srcHandle.kind === 'file') {
            const result = await putFile(srcHandle, destHref, forwardedHeaders, null)
            if (!result.ok) {
                if (result.status === 504 || result.status === 502) {
                    return { status: result.status, body: result.description, sourceDeleted: false }
                }
                return {
                    status: 207,
                    headers: XML_HEADERS,
                    body: multistatusBody([{ href: destHref, status: result.status, description: result.description }]),
                    sourceDeleted: false,
                }
            }
            let sourceDeleted = false
            if (method === 'MOVE') {
                try {
                    await deleteLocal(fileSystemHandler, srcPath)
                    sourceDeleted = true
                } catch (error) {
                    return {
                        status: 207,
                        headers: XML_HEADERS,
                        body: multistatusBody([
                            { href: destHref, status: result.status },
                            { href: srcPath, status: 500, description: `Source delete failed: ${error.message}` },
                        ]),
                        sourceDeleted: false,
                    }
                }
            }
            return { status: result.status === 201 ? 201 : 204, body: '', sourceDeleted }
        }

        // Directory transfer. Depth is ignored (always recursive), like the
        // server's middleware. Deviation from the reference (deliberate,
        // recorded in the parity doc): the local source is deleted only at
        // the end on full success — the server unlinks per-file as it goes.
        const results = []
        await transferDirectory(srcHandle, destHref, forwardedHeaders, results, 0)

        if (results.length > 0) {
            return {
                status: 207,
                headers: XML_HEADERS,
                body: multistatusBody(results),
                sourceDeleted: false,
            }
        }

        let sourceDeleted = false
        if (method === 'MOVE') {
            try {
                await deleteLocal(fileSystemHandler, srcPath)
                sourceDeleted = true
            } catch (error) {
                return {
                    status: 207,
                    headers: XML_HEADERS,
                    body: multistatusBody([
                        { href: destHref, status: 201 },
                        { href: srcPath, status: 500, description: `Source delete failed: ${error.message}` },
                    ]),
                    sourceDeleted: false,
                }
            }
        }
        return { status: 201, body: '', sourceDeleted }
    } finally {
        _activeTransfers--
    }
}

async function deleteLocal(fileSystemHandler, path) {
    const parentPath = path.split('/').slice(0, -1).join('/')
    const name = path.split('/').filter(Boolean).pop()
    const parent = await fileSystemHandler.getFileHandle(parentPath, { getDirectory: true })
    await parent.removeEntry(name, { recursive: true })
    forgetContentEtags(path)
}

// ---------------------------------------------------------------------------
// Pull mode — COPY to this server with `Source: <url>` (HTTP-TPC's pull;
// mirrors localWebDAV's crossServerPull.mjs, 2026-10-17). The destination
// fetches the bytes and writes them through its own streamed PUT, so the
// large-uploads cap on request bodies through a Cloudflare tunnel never
// applies: the transfer is a RESPONSE through the source's edge.
//
// Deviations from the node server, recorded in the parity doc:
// - No `202` with performance markers: a BrowserDAV response is one
//   message, so the status is final — 201 or 204 on success, 207 with the
//   failure inside otherwise. The relay's own transport carries the request
//   while it runs.
// - The outbound guard cannot resolve DNS in a browser: https is required,
//   and literal loopback, private and local hostnames are refused. A Node
//   BrowserDAV may hand in `checkOutbound` to add resolution.
// ---------------------------------------------------------------------------

/** A hostname that is loopback, private or link-local by its spelling. */
export function isLocalOrPrivateHost(hostname) {
    const h = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '')
    if (!h || h === 'localhost' || h.endsWith('.localhost') || h.endsWith('.local')) return true
    const v4 = /^(\d+)\.(\d+)\.(\d+)\.(\d+)$/.exec(h)
    if (v4) {
        const [a, b] = [Number(v4[1]), Number(v4[2])]
        return a === 0 || a === 10 || a === 127 || (a === 169 && b === 254) || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127) || a >= 224
    }
    if (h.includes(':')) {
        if (h === '::' || h === '::1' || h.startsWith('::ffff:') || /^fe[89ab]/.test(h) || /^f[cd]/.test(h) || h.startsWith('ff')) return true
    }
    return false
}

/**
 * May this server fetch `source` on a caller's behalf? https only, no local
 * or private host, no credentials in the URL. `allowInsecure` admits http and
 * local hosts (tests, one-machine setups). `checkOutbound`, when given, is a
 * Node-side resolver check with the final say.
 */
export async function classifySource(source, { allowInsecure = false, checkOutbound = null } = {}) {
    if (!source) return { ok: false, reason: 'Missing Source header' }
    let url
    try {
        url = new URL(source)
    } catch {
        return { ok: false, reason: 'Source must be an absolute URL' }
    }
    if (url.username || url.password) return { ok: false, reason: 'Source must not carry credentials in the URL' }
    if (url.protocol !== 'https:' && !(allowInsecure && url.protocol === 'http:')) {
        return { ok: false, reason: 'Source must be an https URL' }
    }
    if (!allowInsecure && isLocalOrPrivateHost(url.hostname)) {
        return { ok: false, reason: 'Source host is a local or private address' }
    }
    if (checkOutbound) {
        const verdict = await checkOutbound(url.href)
        if (!verdict?.ok) return { ok: false, reason: verdict?.reason || 'Source refused' }
    }
    return { ok: true, url }
}

/** The hrefs of a Depth: 1 PROPFIND answer and whether each is a collection. */
export function parseMultistatus(xml) {
    const out = []
    const responses = String(xml).split(/<(?:[a-zA-Z0-9]+:)?response[\s>]/i).slice(1)
    for (const r of responses) {
        const href = /<(?:[a-zA-Z0-9]+:)?href[^>]*>([^<]*)<\/(?:[a-zA-Z0-9]+:)?href>/i.exec(r)?.[1]
        if (!href) continue
        const collection = /<(?:[a-zA-Z0-9]+:)?collection\s*\/?>/i.test(r)
        out.push({ href: decodeXmlEntities(href.trim()), collection })
    }
    return out
}

function decodeXmlEntities(s) {
    return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&')
}

async function guardedFetch(url, init, guard) {
    let current = url
    for (let i = 0; i <= MAX_REDIRECTS; i++) {
        const resp = await fetch(current, { ...init, redirect: 'manual' })
        if (![301, 302, 303, 307, 308].includes(resp.status)) return resp
        const location = resp.headers.get('location')
        try { await resp.body?.cancel() } catch { /* already gone */ }
        if (!location) throw new Error(`redirect without a Location from ${current}`)
        const next = new URL(location, current).href
        const verdict = await guard(next)
        if (!verdict.ok) throw new Error(`redirect refused: ${verdict.reason}`)
        current = next
    }
    throw new Error('too many redirects')
}

/** An HTTP-TPC performance marker, the node server's shape (crossServerPull.mjs). */
export function perfMarker({ bytes, connections = 1, now = Date.now() }) {
    return `Perf Marker\nTimestamp: ${Math.floor(now / 1000)}\nStripe Bytes Transferred: ${bytes}\nTotal Stripe Count: 1\nRemoteConnections: ${connections}\nEnd\n`
}

const DEFAULT_MARKER_INTERVAL_MS = 5000
/** How many times one pull re-opens its source with a Range before giving up. */
export const MAX_RESUMES = 8
/** The pause before the n-th resume is n times this, capped at five times it (30 s over eight at the default). */
export const RESUME_BACKOFF_MS = 1000

/**
 * Open a source URL as a ReadableStream that resumes itself (mirrors the
 * node server's resumableSource, 2026-10-19). When the source's stream
 * fails part way, the stream re-opens the URL with `Range: bytes=<received>-`
 * and `If-Range: <validator>` and goes on, so the bytes already streamed
 * into the destination's write are never fetched again. Resumable when the
 * first answer carries an ETag or Last-Modified and no Content-Encoding;
 * anything but a 206 at the right offset, an abort, no validator, or the
 * sixth failure errors the stream, and the PUT it feeds fails whole.
 */
export async function resumableSource(url, { headers = {}, signal, guard = async () => ({ ok: true }), maxResumes = MAX_RESUMES, backoffMs = RESUME_BACKOFF_MS, onOpen = null } = {}) {
    const open = async (start, validator) => {
        const h = { ...headers }
        if (start > 0) {
            h['Range'] = `bytes=${start}-`
            if (validator) h['If-Range'] = validator
        }
        const resp = await guardedFetch(url, { headers: h, signal }, guard)
        if (!resp.ok) {
            try { await resp.body?.cancel() } catch { /* gone */ }
            const e = new Error(`source answered ${resp.status}${start > 0 ? ` (resuming at ${start})` : ''}`)
            e.status = resp.status
            throw e
        }
        if (!resp.body) throw new Error('source sent no body')
        if (start > 0) {
            const range = /^bytes (\d+)-(\d+)\/(\d+|\*)$/.exec(resp.headers.get('content-range') || '')
            if (resp.status !== 206 || !range || Number(range[1]) !== start) {
                try { await resp.body.cancel() } catch { /* gone */ }
                const e = new Error(`source cannot resume at ${start}: answered ${resp.status}${resp.headers.get('content-range') ? ` ${resp.headers.get('content-range')}` : ''}`)
                e.permanent = true // the source has no Range for us; trying again changes nothing
                throw e
            }
        }
        onOpen?.()
        return resp
    }
    const first = await open(0, null)
    // The If-Range validator: a STRONG ETag when the source sends one, else
    // Last-Modified. A weak ETag (W/"…", Nephele's) is a 400 from a server
    // that follows RFC 9110, so Nephele sources resume by date; BrowserDAV
    // sources (strong ETag) by tag.
    const etag = first.headers.get('etag')
    const strongEtag = etag && !/^W\//i.test(etag.trim()) ? etag : null
    const validator = strongEtag || first.headers.get('last-modified') || null
    const encoded = !!first.headers.get('content-encoding')
    const lengthHeader = first.headers.get('content-length')
    const contentLength = lengthHeader != null && !encoded ? Number(lengthHeader) : null
    const resumable = !!validator && !encoded
    const state = { received: 0, resumes: 0, resumable }
    let reader = first.body.getReader()
    const body = new ReadableStream({
        async pull(controller) {
            for (;;) {
                try {
                    const { value, done } = await reader.read()
                    if (done) {
                        controller.close()
                        return
                    }
                    state.received += value.byteLength
                    controller.enqueue(value)
                    return
                } catch (error) {
                    if (signal?.aborted) throw error
                    if (contentLength != null && state.received >= contentLength) {
                        controller.close()
                        return
                    }
                    if (!resumable) throw new Error(`${error.message || error} after ${state.received} bytes (source not resumable: ${encoded ? 'encoded body' : 'no ETag or Last-Modified'})`)
                    // Re-open from where we are. A re-open that fails (the
                    // source or its relay still down) counts as a resume too
                    // and is tried again after a longer pause.
                    let resp = null
                    let last = error
                    while (!resp) {
                        if (state.resumes >= maxResumes) throw new Error(`${last.message || last} after ${state.received} bytes; gave up after ${state.resumes} resumes`)
                        state.resumes++
                        await new Promise((r) => setTimeout(r, Math.min(backoffMs * state.resumes, backoffMs * 5)))
                        try {
                            resp = await open(state.received, validator)
                        } catch (e) {
                            if (e.permanent || signal?.aborted) throw e
                            last = e
                        }
                    }
                    reader = resp.body.getReader()
                }
            }
        },
        cancel(reason) {
            reader.cancel(reason).catch(() => {})
        },
    })
    return { body, contentLength, contentType: first.headers.get('content-type') || 'application/octet-stream', state }
}

/**
 * Pull `srcUrl` into `destPath`. The refusals that need no transfer (412,
 * 503) are plain answers. Otherwise the answer is 202 with a text/plain
 * body that streams while the pull runs — a performance marker every
 * `markerIntervalMs`, then `success: Created` / `success: No Content` or
 * `failure: <reason>` — the node server's report, so a relay holding the
 * response open sees bytes before Cloudflare's 100 s and the caller can
 * show progress. The pull runs as soon as the stream is created; a
 * cancelled stream aborts it. `onDone(ok)` runs at the end, before the
 * verdict line, for the caller's cache invalidation.
 */
export async function crossServerPull({
    srcUrl,
    destPath,
    headers = {},
    fileOperations,
    overwrite = true,
    destExists = false,
    guard = async () => ({ ok: true }),
    markerIntervalMs = DEFAULT_MARKER_INTERVAL_MS,
    maxResumes = MAX_RESUMES,
    resumeBackoffMs = RESUME_BACKOFF_MS,
    onDone = null,
}) {
    const forwardedHeaders = extractTransferHeaders(headers)
    if (!overwrite && destExists) {
        return {
            status: 412,
            headers: XML_HEADERS,
            body: `<?xml version="1.0" encoding="utf-8"?>\n<d:error xmlns:d="DAV:"><d:message>Destination resource already exists and Overwrite is F.</d:message></d:error>`,
        }
    }
    if (_activeTransfers >= MAX_CONCURRENT_TRANSFERS) {
        return { status: 503, headers: { 'Retry-After': '5' }, body: 'Too many concurrent transfers' }
    }
    _activeTransfers++
    const controller = new AbortController()
    const progress = { bytes: 0, connections: 0 }
    const encoder = new TextEncoder()
    let streamController = null
    let timer = null
    let closed = false
    const emit = (text) => {
        if (closed || !streamController) return
        try {
            streamController.enqueue(encoder.encode(text))
        } catch {
            closed = true
        }
    }
    const finish = (line) => {
        if (closed) return
        emit(line)
        closed = true
        clearInterval(timer)
        try {
            streamController.close()
        } catch {
            /* already closed */
        }
    }
    const run = async () => {
        let outcome
        try {
            const opts = { progress, signal: controller.signal, maxResumes, backoffMs: resumeBackoffMs }
            if (!srcUrl.pathname.endsWith('/')) {
                const r = await pullFile(srcUrl.href, destPath, forwardedHeaders, fileOperations, guard, opts)
                outcome = r.ok
                    ? { ok: true, line: r.status === 204 ? 'success: No Content\n' : 'success: Created\n' }
                    : { ok: false, line: `failure: ${r.description} (${srcUrl.href})\n` }
            } else {
                const results = []
                await pullTree(srcUrl.href, destPath, forwardedHeaders, fileOperations, guard, results, 0, opts)
                outcome = results.length
                    ? { ok: false, line: `failure: ${results.length} entr${results.length === 1 ? 'y' : 'ies'} not copied — ${results.slice(0, 5).map((r) => `${r.href}: ${r.status} ${r.description || ''}`.trim()).join('; ')}\n` }
                    : { ok: true, line: 'success: Created\n' }
            }
        } catch (error) {
            outcome = { ok: false, line: `failure: ${controller.signal.aborted ? 'cancelled' : (error?.message || String(error))}\n` }
        } finally {
            _activeTransfers--
        }
        try {
            await onDone?.(outcome.ok)
        } catch {
            /* the caller's cache; the transfer stands */
        }
        finish(outcome.line)
    }
    const body = new ReadableStream({
        start(c) {
            streamController = c
            // A first marker at once: a relay that sends headers with the
            // first body byte (the discovery server's HTTP side) then answers
            // the caller immediately, not at the first interval.
            emit(perfMarker({ bytes: 0, connections: 0 }))
            timer = setInterval(() => emit(perfMarker({ bytes: progress.bytes, connections: progress.connections })), markerIntervalMs)
            if (typeof timer.unref === 'function') timer.unref()
            run()
        },
        cancel() {
            closed = true
            clearInterval(timer)
            controller.abort()
        },
    })
    return {
        status: 202,
        headers: { 'Content-Type': 'text/plain; charset=utf-8', 'Cache-Control': 'no-store', 'X-Accel-Buffering': 'no' },
        body,
        // Each marker is its own frame on the relay (groups.js
        // _sendResponseStream), not batched up to the chunk size.
        bodyFlush: 'eager',
    }
}

async function pullFile(href, destPath, forwardedHeaders, fileOperations, guard, { progress = null, signal = undefined, maxResumes = MAX_RESUMES, backoffMs = RESUME_BACKOFF_MS } = {}) {
    let src
    try {
        // The source stream resumes itself with a Range from the bytes
        // already written, so the PUT below sees one body whatever the
        // source connection did (resumableSource).
        src = await resumableSource(href, { headers: forwardedHeaders, signal, guard, maxResumes, backoffMs })
    } catch (error) {
        return { ok: false, status: error.status || 502, description: error.message || 'Network error' }
    }
    if (progress) progress.connections++
    try {
        let body = src.body
        if (progress && typeof TransformStream !== 'undefined') {
            body = body.pipeThrough(new TransformStream({
                transform(chunk, c) {
                    progress.bytes += chunk.byteLength
                    c.enqueue(chunk)
                },
            }))
        }
        const r = await fileOperations.PUT(destPath, body, src.contentType, {})
        return { ok: true, status: r.status }
    } catch (error) {
        return { ok: false, status: error.status || 500, description: error.message || 'write failed' }
    } finally {
        if (progress) progress.connections--
    }
}

async function pullTree(href, destPath, forwardedHeaders, fileOperations, guard, results, depth, opts = {}) {
    if (depth > MAX_COPY_DEPTH) {
        results.push({ href, status: 500, description: 'Max recursion depth exceeded' })
        return
    }
    try {
        await fileOperations.MKCOL(destPath, {})
    } catch (error) {
        if (error?.status !== 405) {
            results.push({ href, status: error?.status || 500, description: `MKCOL failed: ${error?.message || error}` })
            return
        }
    }
    let pf
    try {
        pf = await guardedFetch(href, { method: 'PROPFIND', headers: { ...forwardedHeaders, Depth: '1' }, signal: opts.signal }, guard)
    } catch (error) {
        results.push({ href, status: 502, description: `PROPFIND failed: ${error.message}` })
        return
    }
    const xml = await pf.text()
    if (pf.status !== 207) {
        results.push({ href, status: pf.status, description: 'source PROPFIND failed' })
        return
    }
    const base = new URL(href)
    const basePath = base.pathname.endsWith('/') ? base.pathname : base.pathname + '/'
    for (const e of parseMultistatus(xml)) {
        const url = new URL(e.href, base)
        if (!url.pathname.startsWith(basePath) || url.pathname === basePath || url.pathname === basePath.slice(0, -1)) continue
        const name = decodeURIComponent(url.pathname.slice(basePath.length).replace(/\/$/, ''))
        if (!name || name.includes('/')) continue
        const dest = (destPath.endsWith('/') ? destPath : destPath + '/') + name
        if (e.collection) {
            await pullTree(url.href.endsWith('/') ? url.href : url.href + '/', dest, forwardedHeaders, fileOperations, guard, results, depth + 1, opts)
        } else {
            const r = await pullFile(url.href, dest, forwardedHeaders, fileOperations, guard, opts)
            if (!r.ok) results.push({ href: url.href, status: r.status, description: r.description })
        }
    }
}
